/**
 * Single-stroke vector font for the PDF text FALLBACK path
 * (docs/opencadstudio-parity/02-plot-pdf-ctb.md task 2.6): when no TrueType
 * face can be embedded, text renders as stroked outlines — always legible,
 * never empty. Classic CAD single-case behavior: lowercase maps to its
 * uppercase glyph and Turkish diacritics fold to their base letter (the
 * embedded-font path handles them properly).
 *
 * Glyph cell: y 0 = baseline, y 1 = cap height, x in advance units.
 */

export interface StrokeGlyph {
    /** Advance width in cap-height units. */
    w: number;
    /** Strokes as polylines [x, y] in the glyph cell. */
    s: number[][][];
}

const G = (w: number, ...strokes: number[][][]): StrokeGlyph => ({ w, s: strokes });

/** Uppercase Latin + digits + punctuation (0 = cap height cell). */
const GLYPHS: Record<string, StrokeGlyph> = {
    A: G(0.70, [[0, 0], [0.35, 1], [0.7, 0]], [[0.14, 0.35], [0.56, 0.35]]),
    B: G(0.70, [[0, 0], [0, 1], [0.5, 1], [0.65, 0.85], [0.65, 0.68], [0.5, 0.55], [0, 0.55]], [[0.5, 0.55], [0.68, 0.42], [0.68, 0.16], [0.5, 0]]),
    C: G(0.70, [[0.7, 0.85], [0.55, 1], [0.25, 1], [0, 0.75], [0, 0.25], [0.25, 0], [0.55, 0], [0.7, 0.15]]),
    D: G(0.70, [[0, 0], [0, 1], [0.45, 1], [0.7, 0.75], [0.7, 0.25], [0.45, 0], [0, 0]]),
    E: G(0.65, [[0.65, 1], [0, 1], [0, 0], [0.65, 0]], [[0, 0.55], [0.5, 0.55]]),
    F: G(0.65, [[0.65, 1], [0, 1], [0, 0]], [[0, 0.55], [0.5, 0.55]]),
    G: G(0.75, [[0.75, 0.85], [0.55, 1], [0.25, 1], [0, 0.75], [0, 0.25], [0.25, 0], [0.5, 0], [0.75, 0.15], [0.75, 0.4], [0.45, 0.4]]),
    H: G(0.70, [[0, 0], [0, 1]], [[0.7, 0], [0.7, 1]], [[0, 0.5], [0.7, 0.5]]),
    I: G(0.30, [[0.15, 0], [0.15, 1]]),
    J: G(0.55, [[0.45, 1], [0.45, 0.2], [0.3, 0], [0.1, 0], [0, 0.15]]),
    K: G(0.70, [[0, 0], [0, 1]], [[0.7, 1], [0.05, 0.45]], [[0.25, 0.55], [0.7, 0]]),
    L: G(0.60, [[0, 1], [0, 0], [0.6, 0]]),
    M: G(0.85, [[0, 0], [0, 1], [0.42, 0.3], [0.85, 1], [0.85, 0]]),
    N: G(0.70, [[0, 0], [0, 1], [0.7, 0], [0.7, 1]]),
    O: G(0.75, [[0.25, 1], [0.5, 1], [0.75, 0.75], [0.75, 0.25], [0.5, 0], [0.25, 0], [0, 0.25], [0, 0.75], [0.25, 1]]),
    P: G(0.70, [[0, 0], [0, 1], [0.5, 1], [0.68, 0.85], [0.68, 0.6], [0.5, 0.48], [0, 0.48]]),
    Q: G(0.75, [[0.25, 1], [0.5, 1], [0.75, 0.75], [0.75, 0.25], [0.5, 0], [0.25, 0], [0, 0.25], [0, 0.75], [0.25, 1]], [[0.5, 0.3], [0.8, 0]]),
    R: G(0.70, [[0, 0], [0, 1], [0.5, 1], [0.68, 0.85], [0.68, 0.6], [0.5, 0.48], [0, 0.48]], [[0.35, 0.48], [0.7, 0]]),
    S: G(0.65, [[0.65, 0.85], [0.5, 1], [0.2, 1], [0, 0.82], [0.08, 0.6], [0.5, 0.45], [0.62, 0.3], [0.65, 0.12], [0.45, 0], [0.15, 0], [0, 0.12]]),
    T: G(0.65, [[0, 1], [0.65, 1]], [[0.32, 1], [0.32, 0]]),
    U: G(0.70, [[0, 1], [0, 0.2], [0.18, 0], [0.52, 0], [0.7, 0.2], [0.7, 1]]),
    V: G(0.70, [[0, 1], [0.35, 0], [0.7, 1]]),
    W: G(0.95, [[0, 1], [0.24, 0], [0.48, 0.6], [0.72, 0], [0.95, 1]]),
    X: G(0.70, [[0, 0], [0.7, 1]], [[0, 1], [0.7, 0]]),
    Y: G(0.70, [[0, 1], [0.35, 0.45]], [[0.7, 1], [0.35, 0.45], [0.35, 0]]),
    Z: G(0.65, [[0, 1], [0.65, 1], [0, 0], [0.65, 0]]),
    '0': G(0.70, [[0.25, 1], [0.5, 1], [0.7, 0.75], [0.7, 0.25], [0.5, 0], [0.25, 0], [0, 0.25], [0, 0.75], [0.25, 1]], [[0.15, 0.2], [0.55, 0.8]]),
    '1': G(0.45, [[0.08, 0.8], [0.3, 1], [0.3, 0]]),
    '2': G(0.65, [[0, 0.8], [0.15, 1], [0.45, 1], [0.6, 0.85], [0.6, 0.65], [0, 0], [0.65, 0]]),
    '3': G(0.65, [[0, 1], [0.6, 1], [0.3, 0.55], [0.5, 0.55], [0.62, 0.4], [0.62, 0.2], [0.45, 0], [0.15, 0], [0, 0.12]]),
    '4': G(0.65, [[0.45, 0], [0.45, 1], [0, 0.3], [0.65, 0.3]]),
    '5': G(0.65, [[0.6, 1], [0, 1], [0, 0.55], [0.45, 0.55], [0.62, 0.4], [0.62, 0.18], [0.45, 0], [0.15, 0], [0, 0.1]]),
    '6': G(0.65, [[0.55, 1], [0.25, 1], [0, 0.7], [0, 0.25], [0.25, 0], [0.45, 0], [0.62, 0.15], [0.62, 0.4], [0.45, 0.55], [0, 0.55]]),
    '7': G(0.65, [[0, 1], [0.65, 1], [0.28, 0]]),
    '8': G(0.65, [[0.2, 1], [0.5, 1], [0.62, 0.85], [0.5, 0.7], [0.2, 0.7], [0.08, 0.55], [0.08, 0.15], [0.2, 0], [0.5, 0], [0.62, 0.15], [0.62, 0.55], [0.5, 0.7]], [[0.2, 1], [0.08, 0.85], [0.08, 0.55]], [[0.5, 0.7], [0.62, 0.55]]),
    '9': G(0.65, [[0.1, 0], [0.4, 0], [0.65, 0.3], [0.65, 0.75], [0.4, 1], [0.2, 1], [0.03, 0.85], [0.03, 0.6], [0.2, 0.45], [0.65, 0.45]]),
    ' ': G(0.35),
    '.': G(0.30, [[0.12, 0], [0.13, 0.02]]),
    ',': G(0.30, [[0.13, 0.02], [0.05, -0.15]]),
    '-': G(0.40, [[0, 0.4], [0.4, 0.4]]),
    '+': G(0.50, [[0, 0.4], [0.5, 0.4]], [[0.25, 0.15], [0.25, 0.65]]),
    '=': G(0.50, [[0, 0.3], [0.5, 0.3]], [[0, 0.55], [0.5, 0.55]]),
    ':': G(0.30, [[0.15, 0.58], [0.16, 0.6]], [[0.15, 0], [0.16, 0.02]]),
    ';': G(0.30, [[0.15, 0.58], [0.16, 0.6]], [[0.13, 0.02], [0.05, -0.15]]),
    '/': G(0.50, [[0, 0], [0.5, 1]]),
    '\\': G(0.50, [[0, 1], [0.5, 0]]),
    '(': G(0.35, [[0.3, 1], [0.05, 0.7], [0.05, 0.3], [0.3, 0]]),
    ')': G(0.35, [[0.05, 1], [0.3, 0.7], [0.3, 0.3], [0.05, 0]]),
    '[': G(0.35, [[0.25, 1], [0.05, 1], [0.05, 0], [0.25, 0]]),
    ']': G(0.35, [[0.1, 1], [0.3, 1], [0.3, 0], [0.1, 0]]),
    '<': G(0.50, [[0.45, 0.8], [0.05, 0.45], [0.45, 0.1]]),
    '>': G(0.50, [[0.05, 0.8], [0.45, 0.45], [0.05, 0.1]]),
    '%': G(0.80, [[0.15, 1], [0.65, 0]], [[0.12, 0.75], [0.25, 1], [0.38, 0.75], [0.12, 0.75]], [[0.42, 0.25], [0.55, 0], [0.68, 0.25], [0.42, 0.25]]),
    '*': G(0.50, [[0.25, 0.35], [0.25, 0.95]], [[0, 0.55], [0.5, 0.75]], [[0.5, 0.55], [0, 0.75]]),
    '#': G(0.60, [[0.15, 0], [0.15, 1]], [[0.45, 0], [0.45, 1]], [[0, 0.35], [0.6, 0.35]], [[0, 0.65], [0.6, 0.65]]),
    "'": G(0.20, [[0.1, 0.6], [0.1, 1]]),
    '"': G(0.30, [[0.08, 0.6], [0.08, 1]], [[0.22, 0.6], [0.22, 1]]),
    '!': G(0.30, [[0.15, 0], [0.15, 0.04]], [[0.15, 0.18], [0.15, 1]]),
    '?': G(0.55, [[0, 0.8], [0.15, 1], [0.4, 1], [0.55, 0.85], [0.55, 0.7], [0.28, 0.5], [0.28, 0.35]], [[0.28, 0], [0.28, 0.04]]),
    '_': G(0.50, [[0, -0.08], [0.5, -0.08]]),
    '|': G(0.30, [[0.15, 0], [0.15, 1]]),
    '°': G(0.35, [[0.05, 0.85], [0.15, 1], [0.3, 0.85], [0.15, 0.7], [0.05, 0.85]]),
    '²': G(0.4, [[0.05, 0.55], [0.05, 0.75], [0.3, 0.95], [0.3, 0.55], [0.05, 0.4], [0.05, 0.2], [0.35, 0.2]]),
    '³': G(0.4, [[0.05, 0.95], [0.35, 0.95], [0.15, 0.65], [0.32, 0.65], [0.38, 0.45], [0.2, 0.2], [0.05, 0.2]]),
};

/** Turkish + accented fold table (fallback renders the base letter). */
const FOLD: Record<string, string> = {
    ç: 'C', Ç: 'C', ğ: 'G', Ğ: 'G', ı: 'I', İ: 'I', i: 'I', ö: 'O', Ö: 'O',
    ş: 'S', Ş: 'S', ü: 'U', Ü: 'U', à: 'A', á: 'A', â: 'A', ä: 'A', è: 'E',
    é: 'E', ê: 'E', ë: 'E', ì: 'I', í: 'I', î: 'I', ï: 'I', ò: 'O', ó: 'O',
    ô: 'O', õ: 'O', ù: 'U', ú: 'U', û: 'U', ñ: 'N', Ñ: 'N', ß: 'S',
};

const MISSING: StrokeGlyph = G(0.55, [[0, 0.8], [0.15, 1], [0.4, 1], [0.55, 0.85], [0.55, 0.7], [0.28, 0.5], [0.28, 0.35]], [[0.28, 0], [0.28, 0.04]]);

function glyphOf(ch: string): StrokeGlyph {
    if (GLYPHS[ch]) return GLYPHS[ch];
    const folded = FOLD[ch] ?? ch.toUpperCase();
    return GLYPHS[folded] ?? MISSING;
}

/**
 * Measure a string's advance width at a given cap height.
 */
export function strokeFontWidth(text: string, height: number): number {
    let w = 0;
    for (const ch of text) w += glyphOf(ch).w * 0.75 * height;
    return w;
}

/**
 * Build the outline strokes for one text line anchored at its baseline-left
 * point (already in target space), scaled by `height`. Each stroke is a
 * polyline in target coordinates.
 */
export function strokeFontLines(
    text: string,
    x: number,
    y: number,
    height: number,
    rotationDeg: number,
): [number, number][][] {
    const rad = (rotationDeg * Math.PI) / 180;
    const cos = Math.cos(rad), sin = Math.sin(rad);
    const advance = 0.75 * height;
    const out: [number, number][][] = [];
    let pen = 0;
    for (const ch of text) {
        const glyph = glyphOf(ch);
        glyph.s.forEach((stroke) => {
            if (stroke.length < 2) return;
            out.push(stroke.map(([gx, gy]) => {
                const tx = pen + gx * advance;
                const ty = gy * height;
                return [x + tx * cos - ty * sin, y + tx * sin + ty * cos] as [number, number];
            }));
        });
        pen += glyph.w * advance;
    }
    return out;
}
